// 医生相关API
import apiClient from './config'

/**
 * 医生API类
 */
export class DoctorApi {
  /**
   * 获取医生列表
   * @param {Object} [queryParams] - 查询参数
   * @param {string} [queryParams.keyword] - 关键词搜索（姓名、手机号等）
   * @param {string} [queryParams.department] - 科室筛选
   * @param {number} [queryParams.page] - 页码
   * @param {number} [queryParams.pageSize] - 每页数量
   * @returns {Promise<Object>} 医生列表
   */
  async getDoctorList(queryParams = {}) {
    try {
      console.log('调用获取医生列表API:', queryParams)

      // 调用后端获取医生列表接口
      const response = await apiClient.get('/doctor/list', {
        params: queryParams
      })

      // 检查响应数据结构
      const data = response.data
      console.log('获取医生列表API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '获取医生列表成功',
          data: data.data || [],
          total: data.total || 0
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取医生列表失败'
        console.warn('获取医生列表失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取医生列表异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: [],
          total: 0
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: [],
          total: 0
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '获取医生列表失败，请重试',
          data: [],
          total: 0
        }
      }
    }
  }

  /**
   * 获取当前登录医生的详细信息
   * @returns {Promise<Object>} 当前医生详细信息
   */
  async getCurrentDoctorProfile() {
    try {
      console.log('调用获取当前医生详细信息API')

      // 调用后端获取当前医生信息接口
      const response = await apiClient.get('/doctor/profile/info')

      // 检查响应数据结构
      const data = response.data
      console.log('获取当前医生详细信息API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '获取医生信息成功',
          data: data.data || {}
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取医生信息失败'
        console.warn('获取医生信息失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: {}
        }
      }
    } catch (error) {
      console.error('获取医生信息异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: {}
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: {}
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '获取医生信息失败，请重试',
          data: {}
        }
      }
    }
  }

  /**
   * 获取医生详细信息
   * @param {number} doctorId - 医生ID
   * @returns {Promise<Object>} 医生详细信息
   */
  async getDoctorInfo(doctorId) {
    try {
      console.log('调用获取医生详细信息API:', doctorId)

      // 调用后端获取医生详细信息接口
      const response = await apiClient.get(`/doctor/info/${doctorId}`)

      // 检查响应数据结构
      const data = response.data
      console.log('获取医生详细信息API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '获取医生信息成功',
          data: data.data || {}
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取医生信息失败'
        console.warn('获取医生信息失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: {}
        }
      }
    } catch (error) {
      console.error('获取医生信息异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: {}
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: {}
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '获取医生信息失败，请重试',
          data: {}
        }
      }
    }
  }

  /**
   * 添加医生
   * @param {Object} doctorData - 医生数据
   * @param {string} doctorData.name - 医生姓名
   * @param {string} doctorData.phone - 手机号
   * @param {string} doctorData.email - 邮箱
   * @param {string} doctorData.department - 科室
   * @param {string} doctorData.title - 职称
   * @param {string} [doctorData.speciality] - 专业特长
   * @param {string} [doctorData.introduction] - 个人简介
   * @returns {Promise<Object>} 添加结果
   */
  async addDoctor(doctorData) {
    try {
      console.log('调用添加医生API:', doctorData)

      // 调用后端添加医生接口
      const response = await apiClient.post('/doctor/add', doctorData)

      // 检查响应数据结构
      const data = response.data
      console.log('添加医生API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '添加医生成功',
          data: data.data || {}
        }
      } else {
        // 添加失败
        const errorMessage = data.message || '添加医生失败'
        console.warn('添加医生失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('添加医生异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络'
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '添加医生失败，请重试'
        }
      }
    }
  }

  /**
   * 更新医生信息
   * @param {number} doctorId - 医生ID
   * @param {Object} doctorData - 医生数据
   * @returns {Promise<Object>} 更新结果
   */
  async updateDoctor(doctorId, doctorData) {
    try {
      console.log('调用更新医生信息API:', { doctorId, doctorData })

      // 调用后端更新医生信息接口
      const response = await apiClient.put(`/doctor/update/${doctorId}`, doctorData)

      // 检查响应数据结构
      const data = response.data
      console.log('更新医生信息API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '更新医生信息成功',
          data: data.data || {}
        }
      } else {
        // 更新失败
        const errorMessage = data.message || '更新医生信息失败'
        console.warn('更新医生信息失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('更新医生信息异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络'
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '更新医生信息失败，请重试'
        }
      }
    }
  }

  /**
   * 获取绑定的所有患者的打卡数据
   * @returns {Promise<Object>} 患者打卡数据列表
   */
  async getDoctorPatientsCheckin() {
    try {
      console.log('调用获取患者打卡数据API')

      // 调用后端获取患者打卡数据接口 - 简单的GET请求，无需参数
      const response = await apiClient.get('/checkin-advices/doctor-patients-checkin')

      // 检查响应数据结构
      const data = response.data
      console.log('获取患者打卡数据API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '获取患者打卡数据成功',
          data: data.data || []
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取患者打卡数据失败'
        console.warn('获取患者打卡数据失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: []
        }
      }
    } catch (error) {
      console.error('获取患者打卡数据异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: []
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: []
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '获取患者打卡数据失败，请重试',
          data: []
        }
      }
    }
  }

  /**
   * 删除医生
   * @param {number} doctorId - 医生ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteDoctor(doctorId) {
    try {
      console.log('调用删除医生API:', doctorId)

      // 调用后端删除医生接口
      const response = await apiClient.delete(`/doctor/delete/${doctorId}`)

      // 检查响应数据结构
      const data = response.data
      console.log('删除医生API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '删除医生成功'
        }
      } else {
        // 删除失败
        const errorMessage = data.message || '删除医生失败'
        console.warn('删除医生失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code
        }
      }
    } catch (error) {
      console.error('删除医生异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络'
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '删除医生失败，请重试'
        }
      }
    }
  }

  /**
   * 根据睡眠习惯量表获取患者的打卡建议
   * @param {string} userId - 患者ID
   * @returns {Promise<Object>} 打卡建议列表
   */
  async getSleepHabitCheckinAdvices(userId) {
    try {
      console.log('调用获取睡眠习惯打卡建议API:', { userId })

      const response = await apiClient.get('/checkin-advices/getSleepHabitCheckinAdvices', {
        params: {
          userId: userId
        }
      })

      // 检查响应数据结构
      const data = response.data
      console.log('获取睡眠习惯打卡建议API响应详情:', data)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        // 检查是否有打卡建议数据
        const advices = data.data || []
        console.log('获取到的打卡建议数量:', advices.length)

        return {
          success: true,
          message: data.message || '获取打卡建议成功',
          data: advices,
          hasAdvices: advices.length > 0
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取打卡建议失败'
        console.warn('获取打卡建议失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: [],
          hasAdvices: false
        }
      }
    } catch (error) {
      console.error('获取睡眠习惯打卡建议异常:', error)

      if (error.response) {
        // 服务器返回了错误响应
        const errorData = error.response.data
        const errorMessage = errorData?.message || `服务器错误 (${error.response.status})`

        return {
          success: false,
          message: errorMessage,
          errorCode: error.response.status,
          data: [],
          hasAdvices: false
        }
      } else if (error.request) {
        // 请求发送了但没有收到响应
        return {
          success: false,
          message: '网络连接失败，请检查网络',
          data: [],
          hasAdvices: false
        }
      } else {
        // 其他错误
        return {
          success: false,
          message: error.message || '获取打卡建议失败，请重试',
          data: [],
          hasAdvices: false
        }
      }
    }
  }

  /**
   * 指定量表任务给患者
   * @param {Object} taskData - 任务数据
   * @param {number} taskData.doctorId - 医生ID
   * @param {number} taskData.userId - 患者ID
   * @param {number} taskData.scaleId - 量表ID
   * @param {string} [taskData.deadline] - 任务截止时间
   * @param {string} [taskData.remark] - 任务备注
   * @param {string} taskData.taskPublishTime - 任务发布时间
   * @returns {Promise<Object>} 分配结果
   */
  async assignScaleTaskToPatient(taskData) {
    try {
      console.log('调用指定量表任务给患者API:', taskData)

      const response = await apiClient.post('/scale/response/task/assign', taskData)

      console.log('指定量表任务API响应:', response.data)

      if (response.data?.code === 0) {
        return {
          success: true,
          message: response.data.message || '量表任务分配成功',
          data: response.data.data
        }
      } else {
        return {
          success: false,
          message: response.data?.message || '量表任务分配失败',
          data: null
        }
      }
    } catch (error) {
      console.error('指定量表任务给患者异常:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || '量表任务分配失败，请重试',
        data: null
      }
    }
  }

  /**
   * 发布打卡任务给患者（批量创建）
   * @param {Object} taskData - 任务数据
   * @param {number} taskData.patientId - 患者ID
   * @param {number} taskData.doctorId - 医生ID
   * @param {Array<string>} taskData.advices - 打卡建议列表
   * @param {string} [taskData.startDate] - 开始日期
   * @param {string} [taskData.endDate] - 结束日期
   * @returns {Promise<Object>} 发布结果
   */
  async publishCheckinTasks(taskData) {
    try {
      console.log('调用批量发布打卡任务API:', taskData)

      // 统一计算开始和结束时间，确保所有任务使用相同的时间范围
      // 使用 ?? 运算符，只有在 null 或 undefined 时才使用默认值，空字符串会被保留
      const defaultStartDate = new Date().toISOString().split('T')[0]
      const defaultEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

      // 确保日期格式正确，如果为空字符串或无效值，使用默认值
      const startTime = (taskData.startDate && taskData.startDate.trim())
        ? taskData.startDate.trim()
        : defaultStartDate
      const endTime = (taskData.endDate && taskData.endDate.trim())
        ? taskData.endDate.trim()
        : defaultEndDate

      console.log('统一的时间范围设置:', { startTime, endTime })

      // 准备批量创建的数据，按照新接口文档格式
      // 所有任务使用统一的时间范围
      const checkinTasksArray = taskData.advices.map(advice => ({
        patientUserId: parseInt(taskData.patientId),
        checkinContent: advice,
        startTime: startTime, // 统一使用计算好的开始时间
        endTime: endTime, // 统一使用计算好的结束时间
        isActive: taskData.isActive !== undefined ? taskData.isActive : true
      }))

      console.log('准备批量创建的打卡任务数据:', checkinTasksArray)

      // 调用批量创建API
      const response = await apiClient.post('/checkin-advices/create', checkinTasksArray)

      console.log('批量创建打卡任务API响应:', response.data)

      if (response.data?.code === 0) {
        return {
          success: true,
          message: response.data.message || '打卡任务发布成功',
          data: {
            successCount: taskData.advices.length,
            totalCount: taskData.advices.length,
            message: response.data.data,
            tasks: checkinTasksArray
          }
        }
      } else {
        return {
          success: false,
          message: response.data?.message || '打卡任务发布失败',
          data: {
            successCount: 0,
            totalCount: taskData.advices.length,
            errors: [response.data?.message || '未知错误']
          }
        }
      }
    } catch (error) {
      console.error('批量发布打卡任务异常:', error)

      // 如果批量接口失败，尝试逐个创建
      console.log('批量接口失败，尝试逐个创建打卡任务...')
      try {
        // 统一计算开始和结束时间，确保所有任务使用相同的时间范围
        const defaultStartDate = new Date().toISOString().split('T')[0]
        const defaultEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

        // 确保日期格式正确，如果为空字符串或无效值，使用默认值
        const startTime = (taskData.startDate && taskData.startDate.trim())
          ? taskData.startDate.trim()
          : defaultStartDate
        const endTime = (taskData.endDate && taskData.endDate.trim())
          ? taskData.endDate.trim()
          : defaultEndDate

        console.log('逐个创建时统一的时间范围设置:', { startTime, endTime })

        const individualResults = []

        for (let i = 0; i < taskData.advices.length; i++) {
          const advice = taskData.advices[i]
          const singleTaskData = [{
            patientUserId: parseInt(taskData.patientId),
            checkinContent: advice,
            startTime: startTime, // 统一使用计算好的开始时间
            endTime: endTime, // 统一使用计算好的结束时间
            isActive: taskData.isActive !== undefined ? taskData.isActive : true
          }]

          try {
            const singleResponse = await apiClient.post('/checkin-advices/create', singleTaskData)
            console.log(`第${i + 1}个任务创建响应:`, singleResponse.data)

            if (singleResponse.data?.code === 0) {
              individualResults.push({ success: true, advice })
            } else {
              individualResults.push({
                success: false,
                advice,
                error: singleResponse.data?.message || '创建失败'
              })
            }
          } catch (singleError) {
            console.error(`第${i + 1}个任务创建失败:`, singleError)
            individualResults.push({
              success: false,
              advice,
              error: singleError.message || '创建异常'
            })
          }

          // 添加小延迟避免请求过于频繁
          await new Promise(resolve => setTimeout(resolve, 100))
        }

        const successCount = individualResults.filter(r => r.success).length
        const totalCount = individualResults.length

        return {
          success: successCount > 0,
          message: successCount === totalCount
            ? `成功发布了 ${successCount} 个打卡任务`
            : `发布了 ${successCount}/${totalCount} 个打卡任务，${totalCount - successCount} 个失败`,
          data: {
            successCount,
            totalCount,
            tasks: individualResults.filter(r => r.success).map(r => r.advice),
            errors: individualResults.filter(r => !r.success).map(r => r.error)
          }
        }
      } catch (fallbackError) {
        console.error('逐个创建也失败:', fallbackError)
        return {
          success: false,
          message: error.response?.data?.message || error.message || '打卡任务发布失败，请重试',
          data: {
            successCount: 0,
            totalCount: taskData.advices.length,
            errors: [error.message]
          }
        }
      }
    }
  }

  /**
   * 医生查看绑定患者量表任务分页列表
   * @param {Object} params - 查询参数
   * @param {number} params.current - 当前页码
   * @param {number} params.size - 每页大小
   * @param {number} params.patientId - 患者ID
   * @returns {Promise<Object>} 患者量表任务列表
   */
  async getPatientScaleTasks(params) {
    try {
      console.log('调用获取患者量表任务列表API:', params)

      const response = await apiClient.get('/scale/response/doctor/tasks', {
        params: {
          current: params.current || 1,
          size: params.size || 20,
          patientId: params.patientId
        }
      })

      console.log('患者量表任务列表API响应:', response.data)

      if (response.data?.code === 0) {
        return {
          success: true,
          message: response.data.message || '获取患者量表任务成功',
          data: response.data.data,
          total: response.data.data?.total || 0,
          records: response.data.data?.records || []
        }
      } else {
        return {
          success: false,
          message: response.data?.message || '获取患者量表任务失败',
          data: null,
          total: 0,
          records: []
        }
      }
    } catch (error) {
      console.error('获取患者量表任务列表异常:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || '获取患者量表任务失败，请重试',
        data: null,
        total: 0,
        records: [],
        error: error.response?.data
      }
    }
  }

  /**
   * 检查患者是否完成了睡眠卫生习惯量表
   * @param {number} patientId - 患者ID
   * @returns {Promise<Object>} 量表完成状态和相关信息
   */
  async checkPatientSleepHygieneScale(patientId) {
    try {
      console.log('🔍 开始检查患者睡眠卫生习惯量表完成状态')
      console.log('患者ID:', patientId, '类型:', typeof patientId)

      if (!patientId) {
        console.error('❌ 患者ID为空，无法检查量表状态')
        return {
          success: false,
          hasCompletedScale: false,
          scaleInfo: null,
          message: '患者ID为空，无法检查量表状态',
          error: '患者ID为空'
        }
      }

      // 获取患者的所有量表任务
      console.log('📡 调用API获取患者量表任务列表...')
      const result = await this.getPatientScaleTasks({
        current: 1,
        size: 100, // 获取足够多的记录以确保找到目标量表
        patientId: patientId
      })

      console.log('📡 API调用结果:', result)

      if (!result.success) {
        return {
          success: false,
          hasCompletedScale: false,
          scaleInfo: null,
          message: '获取患者量表任务失败',
          error: result.message
        }
      }

      const records = result.records || []
      console.log('患者量表任务记录:', records)
      console.log('患者量表任务记录详情:', records.map(r => ({
        id: r.id,
        scaleName: r.scaleName,
        status: r.status,
        statusType: typeof r.status,
        statusLength: r.status ? r.status.length : 0,
        score: r.score,
        submitTime: r.submitTime
      })))

      // 添加状态字符串的详细调试信息
      records.forEach((r, index) => {
        if (r.status) {
          console.log(`记录${index + 1}状态详细信息:`, {
            原始状态: r.status,
            状态类型: typeof r.status,
            状态长度: r.status.length,
            前后空格检查: `"${r.status}"`,
            ASCII码: Array.from(r.status).map(char => char.charCodeAt(0)),
            去空格后: r.status.trim(),
            小写后: r.status.toLowerCase()
          })
        }
      })

      // 查找睡眠卫生习惯量表(打卡版) - 增强匹配容错性
      const sleepHygieneScales = records.filter(record => {
        if (!record.scaleName) return false

        const scaleName = String(record.scaleName).trim()
        // 定义睡眠卫生习惯量表的多种可能名称
        const sleepHygieneKeywords = [
          '睡眠卫生习惯量表', '睡眠卫生习惯', '睡眠卫生', '睡眠习惯量表',
          '睡眠习惯', 'sleep hygiene', 'sleep habit', 'SHPS'
        ]

        const hasMatch = sleepHygieneKeywords.some(keyword =>
          scaleName.toLowerCase().includes(keyword.toLowerCase()) ||
          keyword.toLowerCase().includes(scaleName.toLowerCase())
        )

        console.log(`检查量表记录: "${record.scaleName}" -> 匹配结果: ${hasMatch}`)
        if (hasMatch) {
          console.log(`✅ 找到睡眠卫生习惯量表: "${record.scaleName}"`)
        }

        return hasMatch
      })

      console.log('找到的睡眠卫生习惯量表记录:', sleepHygieneScales)
      console.log('睡眠卫生习惯量表记录详情:', sleepHygieneScales.map(s => ({
        id: s.id,
        scaleName: s.scaleName,
        status: s.status,
        score: s.score,
        submitTime: s.submitTime,
        resultExplain: s.resultExplain
      })))

      if (sleepHygieneScales.length === 0) {
        return {
          success: true,
          hasCompletedScale: false,
          scaleInfo: null,
          message: '患者尚未被分配睡眠卫生习惯量表任务',
          allScales: records
        }
      }

      // 查找已完成的睡眠卫生习惯量表
      console.log('开始检查量表完成状态...')
      sleepHygieneScales.forEach((scale, index) => {
        console.log(`量表${index + 1}状态检查: "${scale.scaleName}" -> 状态: "${scale.status}" -> 是否已完成: ${scale.status === '已完成'}`)
      })

      // 定义完成状态的多种可能形式，增强匹配容错性
      const isCompletedStatus = (status) => {
        if (!status) return false
        const normalizedStatus = String(status).trim().toLowerCase()
        const completedStatuses = [
          '已完成', '完成', '已提交', '提交', 'completed', 'submitted', 'finished',
          'done', '1', 'true', 'success', '成功'
        ]
        return completedStatuses.some(completedStatus =>
          normalizedStatus === completedStatus.toLowerCase() ||
          normalizedStatus.includes(completedStatus.toLowerCase()) ||
          completedStatus.toLowerCase().includes(normalizedStatus)
        )
      }

      const completedScale = sleepHygieneScales.find(scale => {
        const isCompleted = isCompletedStatus(scale.status)
        console.log(`详细状态检查: "${scale.scaleName}" -> 原始状态: "${scale.status}" -> 标准化检查: ${isCompleted}`)
        return isCompleted
      })

      console.log('完成状态检查结果:', {
        找到的量表数量: sleepHygieneScales.length,
        已完成的量表: completedScale ? completedScale.scaleName : '无',
        所有状态: sleepHygieneScales.map(s => `${s.scaleName}: ${s.status}`)
      })

      if (completedScale) {
        console.log('✅ 患者已完成睡眠卫生习惯量表:', completedScale)
        return {
          success: true,
          hasCompletedScale: true,
          scaleInfo: completedScale,
          message: '患者已完成睡眠卫生习惯量表',
          completedScales: sleepHygieneScales.filter(s => isCompletedStatus(s.status)),
          allSleepScales: sleepHygieneScales
        }
      } else {
        // 有量表任务但未完成
        return {
          success: true,
          hasCompletedScale: false,
          scaleInfo: sleepHygieneScales[0], // 返回最新的未完成任务
          message: '患者有睡眠卫生习惯量表任务但尚未完成',
          pendingScales: sleepHygieneScales.filter(s => !isCompletedStatus(s.status)),
          allSleepScales: sleepHygieneScales
        }
      }
    } catch (error) {
      console.error('检查患者睡眠卫生习惯量表状态异常:', error)
      return {
        success: false,
        hasCompletedScale: false,
        scaleInfo: null,
        message: '检查量表状态失败',
        error: error.message
      }
    }
  }

  /**
   * 更新打卡建议状态
   * @param {Object} params - 更新参数
   * @param {number} params.id - 打卡建议ID
   * @param {boolean} params.isActive - 是否启用
   * @returns {Promise<Object>} 更新结果
   */
  async updateCheckinAdviceStatus(params) {
    try {
      console.log('更新打卡建议状态:', params)

      const response = await apiClient.put('/checkin-advices/status', null, {
        params: {
          id: params.id,
          isActive: params.isActive
        }
      })

      console.log('更新打卡建议状态API响应:', response.data)

      if (response.data?.code === 0) {
        return {
          success: true,
          message: response.data.message || '状态更新成功',
          data: response.data.data
        }
      } else {
        return {
          success: false,
          message: response.data?.message || '状态更新失败',
          data: null
        }
      }
    } catch (error) {
      console.error('更新打卡建议状态异常:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || '状态更新失败，请重试',
        data: null,
        error: error.response?.data
      }
    }
  }

  /**
   * 更新患者打卡建议
   * @param {Array} advicesData - 打卡建议数据数组
   * @returns {Promise<Object>} 更新结果
   */
  async updatePatientCheckinAdvices(advicesData) {
    try {
      console.log('更新患者打卡建议:', advicesData)

      // 确保数据格式正确
      const formattedData = advicesData.map(advice => ({
        patientUserId: parseInt(advice.patientUserId),
        checkinContent: advice.checkinContent,
        startTime: advice.startTime,
        endTime: advice.endTime,
        isActive: advice.isActive
      }))

      console.log('格式化后的打卡建议数据:', formattedData)

      const response = await apiClient.post('/checkin-advices/update', formattedData)

      console.log('更新患者打卡建议API响应:', response.data)

      if (response.data?.code === 0) {
        return {
          success: true,
          message: response.data.message || '打卡建议更新成功',
          data: response.data.data,
          successCount: formattedData.length,
          failedCount: 0
        }
      } else {
        return {
          success: false,
          message: response.data?.message || '打卡建议更新失败',
          data: null,
          successCount: 0,
          failedCount: formattedData.length,
          errors: [response.data?.message || '打卡建议更新失败']
        }
      }
    } catch (error) {
      console.error('更新患者打卡建议异常:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || '打卡建议更新失败，请重试',
        data: null,
        successCount: 0,
        failedCount: advicesData.length,
        errors: [error.message]
      }
    }
  }
}

// 创建医生API实例
export const doctorApi = new DoctorApi()

// 默认导出
export default doctorApi

